import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PaginatorModule } from "primeng/paginator";
import { TooltipModule } from "primeng/tooltip";
import { TagModule } from "primeng/tag";
import { DetailPageHeaderComponent } from "@/components/shared/detail-page-header/detail-page-header.component";
import { DetailHeaderMetadata } from "@/components/shared/detail-page-header/detail-page-header.model";
import { SharedComponentsModule } from "@/components/shared/shared-components.module";
import { StatsService } from "@/_services";
import { TerminologyService } from "@/_services/terminology/terminology.service";
import { TermKey } from "@/_services/terminology/catalog";
import { PipelineItem } from "@/_models/dashboard-stats.interface";
import { MODULE_ROUTES } from "../safety-pipeline/pipeline-routes.config";

interface StageRow extends PipelineItem {
    ageLabel: string;
    moduleLabel: string;
    routerLink: string[] | null;
}

/**
 * Everything in one pipeline stage (#3011).
 *
 * The panel shows a column's oldest few; this is where its "+N more" goes, so
 * the count on the dashboard leads somewhere rather than only saying how much
 * you cannot see.
 */
@UntilDestroy()
@Component({
    selector: "app-pipeline-stage",
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        PaginatorModule,
        TooltipModule,
        TagModule,
        DetailPageHeaderComponent,
        SharedComponentsModule,
    ],
    templateUrl: "./pipeline-stage.component.html",
    styleUrl: "./pipeline-stage.component.scss",
})
export class PipelineStageComponent implements OnInit {
    readonly pageSize = 25;

    title = "";
    headerMetadata: DetailHeaderMetadata = [];
    overdueLabel = "";
    overdueBadgeLabel = "";
    stage = "";
    total = 0;
    listedTotal = 0;
    currentPage = 0;
    rows: StageRow[] = [];
    rangeLabel = "";
    loading = true;

    constructor(
        private route: ActivatedRoute,
        private statsService: StatsService,
        private terminology: TerminologyService,
    ) {}

    ngOnInit(): void {
        this.route.paramMap.pipe(untilDestroyed(this)).subscribe((params) => {
            this.stage = params.get("stage") ?? "";
            this.title = this.term(`dashboard.pipeline.stage.${this.stage}`);
            this.overdueLabel = this.term("dashboard.pipeline.overdue");
            this.overdueBadgeLabel = this.term(
                "dashboard.pipeline.overdueBadge",
            );
            this.currentPage = 0;
            this.load();
        });
    }

    onPageChange(event: { first: number; rows: number }): void {
        this.currentPage = event.first / event.rows;
        this.load();
    }

    private load(): void {
        this.loading = true;
        this.statsService
            .getPipelineStage(
                this.stage,
                this.currentPage * this.pageSize,
                this.pageSize,
            )
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (page) => {
                    this.total = page.total;
                    this.listedTotal = page.listedTotal;
                    this.rows = page.items.map((item) => this.buildRow(item));
                    this.rangeLabel = this.buildRangeLabel();
                    this.headerMetadata = this.buildHeaderMetadata();
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    private buildRow(item: PipelineItem): StageRow {
        const route = MODULE_ROUTES[item.moduleKey];
        return {
            ...item,
            ageLabel: this.ageLabel(item.ageDays),
            moduleLabel: this.term(`dashboard.module.${item.moduleKey}`),
            routerLink: route ? [...route, item.id] : null,
        };
    }

    /**
     * What the header says about the stage: how much is in it, and how much of
     * that is past its limit, which is the reason to be on this page.
     */
    private buildHeaderMetadata(): DetailHeaderMetadata {
        const flagged = this.rows.filter((row) => row.overdue).length;
        return [
            { label: "In this stage", value: String(this.total) },
            {
                label: "Waiting too long",
                value: `${flagged} on this page`,
                visible: flagged > 0,
            },
        ];
    }

    private buildRangeLabel(): string {
        if (!this.listedTotal) {
            return "";
        }
        const first = this.currentPage * this.pageSize + 1;
        const last = Math.min(first + this.pageSize - 1, this.listedTotal);
        return `${first} - ${last} of ${this.listedTotal}`;
    }

    private ageLabel(days: number): string {
        if (days === 0) {
            return this.term("dashboard.pipeline.ageToday");
        }
        const unit = this.term(
            days === 1
                ? "dashboard.pipeline.ageDay"
                : "dashboard.pipeline.ageDays",
        );
        return `${days} ${unit}`;
    }

    private term(key: string): string {
        return this.terminology.resolve(key as TermKey);
    }
}
